import React, { useRef, useState, useEffect, useContext, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useTerminologia } from '../hooks/useTerminologia';

const HOST_API = process.env.REACT_APP_URL;

const FORMAS_PAGO = [
    { label: 'Efectivo',             value: 1 },
    { label: 'Transferencia',        value: 2 },
    { label: 'Cheque',               value: 3 },
    { label: 'Débito automático',    value: 4 },
];

const BANCOS = [
    { label: 'Galicia',      value: 'Galicia' },
    { label: 'Hipotecario',  value: 'Hipotecario' },
    { label: 'Macro',        value: 'Macro' },
    { label: 'MercadoPago',  value: 'MercadoPago' },
    { label: 'Santander',    value: 'Santander' },
];

const CONCEPTOS = [
    { label: 'Expensa',       value: 1 },
    { label: 'Plan de pago',  value: 2 },
];

const PAGO_EMPTY = {
    unidadFuncionalId: null,
    fechaPago:         null,
    importe:           null,
    formaPagoId:       null,
    detalle:           '',
    periodo:           null,
    conceptoPagoId:    1,
    planPagoCuotaId:   null,
    esImportado:       false,
};

const fmtFecha  = (v) => v ? new Date(v).toLocaleDateString('es-AR') : '—';
const fmtImporte = (v) => v != null ? `$ ${Number(v).toLocaleString('es-AR', { minimumFractionDigits: 2 })}` : '—';
const fmtPeriodo = (v) => v
    ? new Date(v).toLocaleDateString('es-AR', { month: 'long', year: 'numeric' })
    : '—';

const FormularioPago = () => {
    const toast = useRef(null);
    const fileInputRef = useRef(null);
    const { barrio } = useContext(BarrioContext);
    const t = useTerminologia();

    const administracionId = parseInt(localStorage.getItem('administracionId') || '1');
    const usuarioId = JSON.parse(localStorage.getItem('user') || '{}').id
        || JSON.parse(localStorage.getItem('user') || '{}').usuarioId
        || 0;

    // ─── Data ────────────────────────────────────────────────────────────────────
    const [pagos,  setPagos]  = useState([]);
    const [ufs,    setUfs]    = useState([]);
    const [loading, setLoading] = useState(false);

    // ─── Planes de pago y cuotas (para los combos del modal) ──────────────────────
    const [planes, setPlanes] = useState([]);
    const [loadingPlanes, setLoadingPlanes] = useState(false);
    const [planPagoId, setPlanPagoId] = useState(null);
    const [cuotas, setCuotas] = useState([]);
    const [loadingCuotas, setLoadingCuotas] = useState(false);

    // ─── Filtros ─────────────────────────────────────────────────────────────────
    const [filtroUF,      setFiltroUF]      = useState(null);
    const [filtroPeriodo, setFiltroPeriodo] = useState(null);

    // ─── Modal registro manual ────────────────────────────────────────────────────
    const [modalPago,  setModalPago]  = useState(false);
    const [form,       setForm]       = useState({ ...PAGO_EMPTY });
    const [submitting, setSubmitting] = useState(false);

    // ─── Modal importar Excel ─────────────────────────────────────────────────────
    const [modalExcel,    setModalExcel]    = useState(false);
    const [excelFile,     setExcelFile]     = useState(null);
    const [excelPeriodo,  setExcelPeriodo]  = useState(null);
    const [excelBanco,    setExcelBanco]    = useState(null);
    const [importing,     setImporting]     = useState(false);

    // ─── Carga UFs ───────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!barrio?.id) return;
        axios.get(`${HOST_API}api/unidadesFuncionales/getUnidadesFuncionales?unidadFuncionalId=0&administracionId=${administracionId}&barrioId=${barrio.id}`)
            .then(({ data }) => setUfs(data))
            .catch(() => {});
    }, [barrio?.id]);

    // ─── Carga planes de pago de la UF seleccionada ───────────────────────────────
    useEffect(() => {
        setPlanPagoId(null);
        setCuotas([]);
        if (!modalPago || form.conceptoPagoId !== 2 || !form.unidadFuncionalId) {
            setPlanes([]);
            return;
        }
        setLoadingPlanes(true);
        axios.get(`${HOST_API}api/PlanPago/GetPlanesPago`, {
            params: { administracionId, unidadFuncionalId: form.unidadFuncionalId },
        })
            .then(({ data }) => setPlanes((data || []).filter(p => p.saldo > 0)))
            .catch(() => setPlanes([]))
            .finally(() => setLoadingPlanes(false));
    }, [modalPago, form.conceptoPagoId, form.unidadFuncionalId]);

    // ─── Carga cuotas del plan de pago seleccionado ────────────────────────────────
    useEffect(() => {
        if (!planPagoId) {
            setCuotas([]);
            return;
        }
        setLoadingCuotas(true);
        axios.get(`${HOST_API}api/PlanPago/GetPlanPagoCuotas`, {
            params: { administracionId, planPagoId },
        })
            .then(({ data }) => setCuotas((data || []).filter(c => c.saldo > 0)))
            .catch(() => setCuotas([]))
            .finally(() => setLoadingCuotas(false));
    }, [planPagoId]);

    const planLabel = (p) =>
        `${p.concepto} · ${p.cantidadCuotas} cuotas · saldo $ ${Number(p.saldo).toLocaleString('es-AR')} · ${p.estadoPlanPago}`;

    const cuotaLabel = (c) =>
        `Cuota ${c.numeroCuota}/${c.cantidadCuotas} · $ ${Number(c.importeTotal).toLocaleString('es-AR')} (saldo $ ${Number(c.saldo).toLocaleString('es-AR')})`;

    const seleccionarPlan = (id) => {
        setPlanPagoId(id);
        setForm(f => ({ ...f, planPagoCuotaId: null }));
    };

    const seleccionarCuota = (planPagoCuotaId) => {
        const cuota = cuotas.find(c => c.planPagoCuotaId === planPagoCuotaId);
        setForm(f => ({
            ...f,
            planPagoCuotaId,
            importe: cuota ? cuota.saldo : f.importe,
            periodo: cuota ? new Date(cuota.periodo) : f.periodo,
        }));
    };

    // ─── Carga pagos ─────────────────────────────────────────────────────────────
    const fetchPagos = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            let url = `${HOST_API}api/pagosUF/GetPagos?administracionId=${administracionId}&barrioId=${barrio.id}`;
            if (filtroUF)      url += `&unidadFuncionalId=${filtroUF}`;
            if (filtroPeriodo) url += `&periodo=${filtroPeriodo.toISOString()}`;
            const { data } = await axios.get(url);
            setPagos(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los pagos', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, filtroUF, filtroPeriodo]);

    useEffect(() => { fetchPagos(); }, [fetchPagos]);

    // ─── Registrar pago manual ────────────────────────────────────────────────────
    const guardarPago = async () => {
        if (!form.unidadFuncionalId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione la unidad funcional', life: 3000 }); return; }
        if (!form.periodo)           { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el período', life: 3000 }); return; }
        if (!form.fechaPago)         { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese la fecha de pago', life: 3000 }); return; }
        if (!form.importe)           { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Ingrese el importe', life: 3000 }); return; }
        if (!form.formaPagoId)       { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione la forma de pago', life: 3000 }); return; }
        if (form.conceptoPagoId === 2 && !form.planPagoCuotaId) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione la cuota del plan de pago', life: 3000 }); return; }

        const ufSeleccionada = ufs.find(u => u.unidadFuncionalId === form.unidadFuncionalId);

        setSubmitting(true);
        try {
            const payload = {
                unidadFuncionalId: form.unidadFuncionalId,
                unidadFuncional:   ufSeleccionada?.unidadFuncional || null,
                barrioId:          barrio.id,
                administracionId,
                fechaPago:         form.fechaPago,
                importe:           form.importe,
                formaPagoId:       form.formaPagoId,
                detalle:           form.detalle || null,
                usuarioId,
                periodo:           form.periodo,
                conceptoPagoId:    form.conceptoPagoId,
                planPagoCuotaId:   form.planPagoCuotaId || null,
                esImportado:       false,
                autoAprobar:       true,
            };
            await axios.post(`${HOST_API}api/pagosUF/RegistrarPago`, payload);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Pago registrado correctamente', life: 3000 });
            setModalPago(false);
            setForm({ ...PAGO_EMPTY });
            fetchPagos();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'No se pudo registrar el pago', life: 4000 });
        } finally {
            setSubmitting(false);
        }
    };

    // ─── Aprobar / Rechazar pago pendiente ────────────────────────────────────────
    const aprobarPago = (pago) => {
        confirmDialog({
            message:      `¿Aprobar el pago de $ ${Number(pago.importe).toLocaleString('es-AR')} de UF ${pago.unidadFuncional}?`,
            header:       'Confirmar aprobación',
            icon:         'pi pi-check-circle',
            acceptLabel:  'Aprobar',
            rejectLabel:  'Cancelar',
            accept: async () => {
                try {
                    await axios.post(`${HOST_API}api/pagosUF/AprobarPago?pagoId=${pago.pagoId}&administracionId=${administracionId}`);
                    toast.current.show({ severity: 'success', summary: 'Aprobado', detail: 'Pago aprobado', life: 3000 });
                    fetchPagos();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo aprobar el pago', life: 3000 });
                }
            },
        });
    };

    const rechazarPago = (pago) => {
        confirmDialog({
            message:      `¿Rechazar el pago de $ ${Number(pago.importe).toLocaleString('es-AR')} de UF ${pago.unidadFuncional}?`,
            header:       'Confirmar rechazo',
            icon:         'pi pi-times-circle',
            acceptLabel:  'Rechazar',
            rejectLabel:  'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.post(`${HOST_API}api/pagosUF/RechazarPago?pagoId=${pago.pagoId}&administracionId=${administracionId}`);
                    toast.current.show({ severity: 'success', summary: 'Rechazado', detail: 'Pago rechazado', life: 3000 });
                    fetchPagos();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo rechazar el pago', life: 3000 });
                }
            },
        });
    };

    // ─── Anular pago ──────────────────────────────────────────────────────────────
    const anularPago = (pago) => {
        confirmDialog({
            message:      `¿Anular el pago de $ ${Number(pago.importe).toLocaleString('es-AR')} de UF ${pago.unidadFuncional}?`,
            header:       'Confirmar anulación',
            icon:         'pi pi-exclamation-triangle',
            acceptLabel:  'Anular',
            rejectLabel:  'Cancelar',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.post(`${HOST_API}api/pagosUF/AnularPagoUnidadFuncional?pagoId=${pago.pagoId}&administracionId=${administracionId}`);
                    toast.current.show({ severity: 'success', summary: 'Anulado', detail: 'Pago anulado', life: 3000 });
                    fetchPagos();
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo anular el pago', life: 3000 });
                }
            },
        });
    };

    // ─── Importar Excel ───────────────────────────────────────────────────────────
    const handleExcelChange = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        const ext = f.name.split('.').pop().toLowerCase();
        if (!['xlsx', 'xls'].includes(ext)) {
            toast.current.show({ severity: 'warn', summary: 'Formato inválido', detail: 'Solo se aceptan archivos .xlsx o .xls', life: 3000 });
            return;
        }
        setExcelFile(f);
    };

    const importarExcel = async () => {
        if (!excelBanco)   { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el banco', life: 3000 }); return; }
        if (!excelFile)    { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione un archivo Excel', life: 3000 }); return; }
        if (!excelPeriodo) { toast.current.show({ severity: 'warn', summary: 'Requerido', detail: 'Seleccione el período', life: 3000 }); return; }

        setImporting(true);
        try {
            const fd = new FormData();
            fd.append('file',             excelFile);
            fd.append('barrioId',         barrio.id);
            fd.append('administracionId', administracionId);
            fd.append('usuarioId',        usuarioId);
            fd.append('periodo',          excelPeriodo.toISOString());
            fd.append('banco',            excelBanco);

            await axios.post(`${HOST_API}api/pagosUF/ImportarExcel`, fd, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            toast.current.show({ severity: 'success', summary: 'Importación exitosa', detail: 'Los pagos fueron importados correctamente', life: 4000 });
            setModalExcel(false);
            setExcelFile(null);
            setExcelPeriodo(null);
            setExcelBanco(null);
            fetchPagos();
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.errorDisplay || 'Error al importar el archivo', life: 4000 });
        } finally {
            setImporting(false);
        }
    };

    // Descarga una plantilla Excel mínima (CSV válido) para que el usuario sepa el formato
    const descargarPlantilla = () => {
        const csv = 'UnidadFuncional;FechaPago;Importe;Detalle\n1;01/07/2026;15000;Pago expensa julio\n2;01/07/2026;12500;\n';
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = 'plantilla_pagos.csv';
        a.click();
        URL.revokeObjectURL(url);
    };

    // ─── Templates de columna ─────────────────────────────────────────────────────
    const importeTemplate  = (row) => fmtImporte(row.importe);
    const fechaTemplate    = (row) => fmtFecha(row.fechaPago);
    const periodoTemplate  = (row) => fmtPeriodo(row.periodo);
    const ESTADO_COLORES = {
        Pendiente: { bg: '#fffaeb', color: '#b54708', border: '#fedf89' },
        Aprobado:  { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
        Rechazado: { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
    };
    const estadoTemplate = (row) => {
        const s = ESTADO_COLORES[row.estado] || { bg: '#f2f4f7', color: '#344054', border: '#eaecf0' };
        return (
            <span style={{
                background: s.bg, color: s.color, border: `1px solid ${s.border}`,
                fontWeight: 600, fontSize: '0.75rem', padding: '0.15rem 0.6rem', borderRadius: 99,
            }}>
                {row.estado}
            </span>
        );
    };
    const accionesTemplate = (row) => (
        <div className="flex gap-1 justify-content-center">
            {row.estado === 'Pendiente' && (
                <>
                    <Button icon="pi pi-check" className="p-button-rounded p-button-success p-button-text p-button-sm"
                        tooltip="Aprobar" tooltipOptions={{ position: 'top' }}
                        onClick={() => aprobarPago(row)} />
                    <Button icon="pi pi-times" className="p-button-rounded p-button-warning p-button-text p-button-sm"
                        tooltip="Rechazar" tooltipOptions={{ position: 'top' }}
                        onClick={() => rechazarPago(row)} />
                </>
            )}
            <Button icon="pi pi-ban" className="p-button-rounded p-button-danger p-button-text p-button-sm"
                tooltip="Anular" tooltipOptions={{ position: 'top' }}
                onClick={() => anularPago(row)} />
        </div>
    );

    // ─── Render ───────────────────────────────────────────────────────────────────
    return (
        <>
            <h4>Pagos</h4>
            <br />
            <Toast ref={toast} />
            <ConfirmDialog />

            {loading && (
                <div style={{ position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ProgressSpinner style={{ width: 52, height: 52 }} strokeWidth="4" />
                </div>
            )}

            {/* ─── Barra superior ──────────────────────────────────────────────── */}
            <div className="flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div className="flex gap-2 flex-wrap align-items-end">
                    <div>
                        <label style={lbl}>{t.UF}</label>
                        <Dropdown value={filtroUF} options={ufs} optionLabel="unidadFuncional" optionValue="unidadFuncionalId"
                            onChange={(e) => setFiltroUF(e.value)} placeholder="Todas" showClear filter
                            className="p-inputtext-sm" style={{ width: 160 }} />
                    </div>
                    <div>
                        <label style={lbl}>Período</label>
                        <Calendar value={filtroPeriodo} onChange={(e) => setFiltroPeriodo(e.value)}
                            view="month" dateFormat="mm/yy" placeholder="Todos" showButtonBar
                            className="p-inputtext-sm" style={{ width: 140 }} />
                    </div>
                    <Button label="Buscar" icon="pi pi-search" onClick={fetchPagos} className="p-button-sm" />
                </div>
                <div className="flex gap-2">
                    <Button label="Importar Excel" icon="pi pi-file-excel" className="p-button-sm p-button-success"
                        onClick={() => { setModalExcel(true); setExcelFile(null); setExcelPeriodo(null); setExcelBanco(null); }} />
                    <Button label="Registrar Pago" icon="pi pi-plus" className="p-button-sm"
                        onClick={() => { setForm({ ...PAGO_EMPTY }); setModalPago(true); }} />
                </div>
            </div>

            {/* ─── Grilla ──────────────────────────────────────────────────────── */}
            <div className="grid">
                <div className="col-12">
                    <DataTable value={pagos} paginator rows={15} rowsPerPageOptions={[10, 15, 25, 50]}
                        showGridlines responsiveLayout="scroll"
                        emptyMessage="No hay pagos registrados"
                        className="p-datatable-gridlines bg-white"
                        sortField="fechaPago" sortOrder={-1}>
                        <Column field="unidadFuncional"  header={t.UF}          sortable style={{ width: '5rem', textAlign: 'center' }} />
                        <Column field="persona"          header="Persona"      sortable />
                        <Column field="razonSocial"      header={t.Barrio}       sortable style={{ width: '10rem' }} />
                        <Column field="periodo"          header="Período"      sortable body={periodoTemplate} style={{ width: '9rem' }} />
                        <Column field="fechaPago"        header="Fecha pago"   sortable body={fechaTemplate}   style={{ width: '8rem' }} />
                        <Column field="importe"          header="Importe"      sortable body={importeTemplate} style={{ width: '9rem', textAlign: 'right' }} />
                        <Column field="formaPago"        header="Forma pago"   sortable style={{ width: '9rem' }} />
                        <Column field="detalle"          header="Detalle"      body={(r) => r.detalle || '—'} />
                        <Column field="usuario"          header="Registrado por" sortable style={{ width: '10rem' }} />
                        <Column header="Estado" body={estadoTemplate} style={{ width: '7rem' }} />
                        <Column header="Acciones" body={accionesTemplate} style={{ width: '10rem', textAlign: 'center' }} />
                    </DataTable>
                </div>
            </div>

            {/* ─── Modal: Registrar Pago ────────────────────────────────────────── */}
            <Dialog header="Registrar Pago" visible={modalPago} style={{ width: '480px' }} modal
                onHide={() => setModalPago(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalPago(false)} disabled={submitting} />
                        <Button label="Guardar" icon="pi pi-check" className="p-button-sm" onClick={guardarPago} loading={submitting} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    <div className="col-12">
                        <label style={lbl}>{t.UnidadFuncional} *</label>
                        <Dropdown value={form.unidadFuncionalId} options={ufs} optionLabel="unidadFuncional" optionValue="unidadFuncionalId"
                            onChange={(e) => setForm(f => ({ ...f, unidadFuncionalId: e.value }))}
                            placeholder={`Seleccionar ${t.UF}`} filter className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Período *</label>
                        <Calendar value={form.periodo} onChange={(e) => setForm(f => ({ ...f, periodo: e.value }))}
                            view="month" dateFormat="mm/yy" placeholder="mm/aaaa" className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Fecha de pago *</label>
                        <Calendar value={form.fechaPago} onChange={(e) => setForm(f => ({ ...f, fechaPago: e.value }))}
                            dateFormat="dd/mm/yy" placeholder="dd/mm/aaaa" className="p-inputtext-sm" showIcon />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Importe *</label>
                        <InputNumber value={form.importe} onValueChange={(e) => setForm(f => ({ ...f, importe: e.value }))}
                            mode="currency" currency="ARS" locale="es-AR" className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Forma de pago *</label>
                        <Dropdown value={form.formaPagoId} options={FORMAS_PAGO}
                            onChange={(e) => setForm(f => ({ ...f, formaPagoId: e.value }))}
                            placeholder="Seleccionar" className="p-inputtext-sm" />
                    </div>
                    <div className="col-6">
                        <label style={lbl}>Concepto</label>
                        <Dropdown value={form.conceptoPagoId} options={CONCEPTOS}
                            onChange={(e) => setForm(f => ({ ...f, conceptoPagoId: e.value, planPagoCuotaId: null }))}
                            className="p-inputtext-sm" />
                    </div>
                    {form.conceptoPagoId === 2 && (
                        <>
                            <div className="col-12">
                                <label style={lbl}>Plan de pago *</label>
                                <Dropdown value={planPagoId} options={planes}
                                    optionLabel="planPagoId" optionValue="planPagoId"
                                    itemTemplate={planLabel}
                                    valueTemplate={(option) => option ? planLabel(option) : 'Seleccionar plan'}
                                    onChange={(e) => seleccionarPlan(e.value)}
                                    placeholder={!form.unidadFuncionalId ? 'Primero seleccioná la UF' : 'Seleccionar plan'}
                                    disabled={!form.unidadFuncionalId}
                                    loading={loadingPlanes}
                                    emptyMessage="Esta UF no tiene planes de pago con saldo"
                                    className="p-inputtext-sm" />
                            </div>
                            <div className="col-12">
                                <label style={lbl}>Cuota *</label>
                                <Dropdown value={form.planPagoCuotaId} options={cuotas}
                                    optionLabel="planPagoCuotaId" optionValue="planPagoCuotaId"
                                    itemTemplate={cuotaLabel}
                                    valueTemplate={(option) => option ? cuotaLabel(option) : 'Seleccionar cuota'}
                                    onChange={(e) => seleccionarCuota(e.value)}
                                    placeholder={!planPagoId ? 'Primero seleccioná el plan' : 'Seleccionar cuota'}
                                    disabled={!planPagoId}
                                    loading={loadingCuotas}
                                    emptyMessage="Este plan no tiene cuotas pendientes"
                                    className="p-inputtext-sm" />
                            </div>
                        </>
                    )}
                    <div className="col-12">
                        <label style={lbl}>Detalle</label>
                        <InputText value={form.detalle} onChange={(e) => setForm(f => ({ ...f, detalle: e.target.value }))}
                            placeholder="Observaciones opcionales" className="p-inputtext-sm" />
                    </div>
                </div>
            </Dialog>

            {/* ─── Modal: Importar Excel ────────────────────────────────────────── */}
            <Dialog header="Importar Pagos desde Excel" visible={modalExcel} style={{ width: '460px' }} modal
                onHide={() => setModalExcel(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancelar" className="p-button-text p-button-sm" onClick={() => setModalExcel(false)} disabled={importing} />
                        <Button label="Importar" icon="pi pi-upload" className="p-button-sm p-button-success" onClick={importarExcel} loading={importing} />
                    </div>
                }>
                <div className="grid p-fluid mt-1">
                    {/* Formato esperado */}
                    {/* <div className="col-12">
                        <div style={{ background: '#f8f9fa', border: '1px solid #e4e7ec', borderRadius: 8, padding: '0.75rem 1rem', fontSize: '0.82rem', color: '#344054' }}>
                            <strong>Formato del archivo:</strong><br />
                            <span style={{ color: '#667085' }}>Columnas (sin encabezado de datos, fila 1 = títulos):</span>
                            <ol style={{ margin: '0.35rem 0 0 1rem', padding: 0, lineHeight: 1.8 }}>
                                <li><b>A:</b> {t.UnidadFuncional}</li>
                                <li><b>B:</b> Fecha de pago (dd/mm/aaaa)</li>
                                <li><b>C:</b> Importe (ej: 15000.00)</li>
                                <li><b>D:</b> Detalle (opcional)</li>
                            </ol>
                        </div>
                        <Button label="Descargar plantilla" icon="pi pi-download" className="p-button-text p-button-sm mt-2"
                            onClick={descargarPlantilla} />
                    </div> */}

                    <div className="col-12">
                        <label style={lbl}>Banco *</label>
                        <Dropdown value={excelBanco} options={BANCOS} onChange={(e) => setExcelBanco(e.value)}
                            placeholder="Seleccione el banco" className="p-inputtext-sm" />
                    </div>

                    <div className="col-12">
                        <label style={lbl}>Período *</label>
                        <Calendar value={excelPeriodo} onChange={(e) => setExcelPeriodo(e.value)}
                            view="month" dateFormat="mm/yy" placeholder="mm/aaaa" className="p-inputtext-sm" />
                    </div>

                    <div className="col-12">
                        <label style={lbl}>Archivo Excel (.xlsx / .xls) *</label>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <input ref={fileInputRef} type="file" accept=".xlsx,.xls" style={{ display: 'none' }}
                                onChange={handleExcelChange} />
                            <Button label={excelFile ? excelFile.name : 'Seleccionar archivo'}
                                icon="pi pi-file-excel"
                                className={`p-button-outlined p-button-sm ${excelFile ? 'p-button-success' : ''}`}
                                style={{ flex: 1, justifyContent: 'flex-start', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                onClick={() => fileInputRef.current.click()} />
                            {excelFile && (
                                <Button icon="pi pi-times" className="p-button-rounded p-button-text p-button-danger p-button-sm"
                                    onClick={() => { setExcelFile(null); fileInputRef.current.value = ''; }} />
                            )}
                        </div>
                    </div>
                </div>
            </Dialog>
        </>
    );
};

const lbl = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };

export default FormularioPago;
